import React from 'react';
import { Users, Target, Heart, MessageCircle } from 'lucide-react';

const Stats = () => {
  const stats = [
    { 
      number: '50,000+', 
      label: 'Active Parents', 
      icon: Users,
      description: 'Growing community worldwide',
      gradientBg: 'from-pink-50 to-rose-100',
      gradientIcon: 'from-pink-400 to-rose-500',
      shadowColor: 'shadow-pink-100'
    },
    { 
      number: '1M+', 
      label: 'Milestones Tracked', 
      icon: Target,
      description: 'Celebrating achievements daily',
      gradientBg: 'from-blue-50 to-indigo-100',
      gradientIcon: 'from-blue-400 to-indigo-500',
      shadowColor: 'shadow-blue-100'
    },
    { 
      number: '98%', 
      label: 'Parent Satisfaction', 
      icon: Heart,
      description: 'Loved by families everywhere',
      gradientBg: 'from-purple-50 to-violet-100',
      gradientIcon: 'from-purple-400 to-violet-500',
      shadowColor: 'shadow-purple-100'
    },
    { 
      number: '24/7', 
      label: 'Expert Support', 
      icon: MessageCircle,
      description: 'Always here when you need us',
      gradientBg: 'from-green-50 to-emerald-100',
      gradientIcon: 'from-green-400 to-emerald-500',
      shadowColor: 'shadow-green-100'
    }
  ];

  return (
    <section className="py-32 bg-gradient-to-b from-white via-gradient-to-r from-pink-50/30 to-purple-50/30 to-white relative overflow-hidden">
      {/* Layered organic curved backgrounds */}
      <div className="absolute inset-0">
        <svg className="absolute top-0 left-0 w-full h-64" viewBox="0 0 1440 256" preserveAspectRatio="none">
          <path d="M0,128 Q360,64 720,128 T1440,128 L1440,0 L0,0 Z" 
                fill="url(#wave-gradient-1)" opacity="0.03"/>
          <path d="M0,160 Q360,96 720,160 T1440,160 L1440,0 L0,0 Z" 
                fill="url(#wave-gradient-2)" opacity="0.03"/>
          <path d="M0,192 Q360,128 720,192 T1440,192 L1440,0 L0,0 Z" 
                fill="url(#wave-gradient-3)" opacity="0.03"/>
          <defs>
            <linearGradient id="wave-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FEC5E5"/>
              <stop offset="100%" stopColor="#C7B5FD"/>
            </linearGradient>
            <linearGradient id="wave-gradient-2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#93C5FD"/>
              <stop offset="100%" stopColor="#FEC5E5"/>
            </linearGradient>
            <linearGradient id="wave-gradient-3" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#C7B5FD"/>
              <stop offset="100%" stopColor="#93C5FD"/>
            </linearGradient>
          </defs>
        </svg>
        
        <svg className="absolute bottom-0 left-0 w-full h-64" viewBox="0 0 1440 256" preserveAspectRatio="none">
          <path d="M0,128 Q360,192 720,128 T1440,128 L1440,256 L0,256 Z" 
                fill="url(#wave-gradient-4)" opacity="0.03"/>
          <path d="M0,96 Q360,160 720,96 T1440,96 L1440,256 L0,256 Z" 
                fill="url(#wave-gradient-5)" opacity="0.03"/>
          <defs>
            <linearGradient id="wave-gradient-4" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FEC5E5"/>
              <stop offset="50%" stopColor="#C7B5FD"/>
              <stop offset="100%" stopColor="#93C5FD"/>
            </linearGradient>
            <linearGradient id="wave-gradient-5" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0C1D40" stopOpacity="0.1"/>
              <stop offset="100%" stopColor="#1A2B50" stopOpacity="0.1"/>
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Subtle floating orbs */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-br from-pink-200 to-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
      <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-br from-blue-200 to-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      <div className="absolute top-40 right-1/3 w-48 h-48 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center bg-gradient-to-r from-pink-50 to-purple-50 backdrop-blur-sm rounded-full px-6 py-3 mb-8 shadow-sm border border-white/50">
            <span className="relative flex h-2 w-2 mr-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-sm font-semibold text-[#0C1D40] uppercase tracking-wider">Trusted by families worldwide</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0C1D40] mb-6">
            Growing Together, <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">Every Day</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Join thousands of parents using Tifl to track, celebrate, and support their child's incredible journey
          </p>
        </div>

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-7xl mx-auto mb-20">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="group cursor-pointer">
                <div className="relative h-full">
                  {/* Card with advanced styling */}
                  <div className={`relative bg-white rounded-3xl p-8 lg:p-10 h-full transition-all duration-500 transform hover:-translate-y-3 hover:scale-105 shadow-xl hover:shadow-2xl ${stat.shadowColor} border border-gray-50/50`}>
                    {/* Gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradientBg} opacity-0 group-hover:opacity-100 rounded-3xl transition-opacity duration-500`}></div>
                    
                    {/* Decorative corner accent */}
                    <div className={`absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br ${stat.gradientIcon} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity duration-500`}></div>
                    
                    {/* Content */}
                    <div className="relative text-center space-y-4">
                      {/* Enhanced icon container */}
                      <div className="mx-auto">
                        <div className={`relative inline-flex items-center justify-center w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br ${stat.gradientBg} rounded-2xl group-hover:rounded-3xl transition-all duration-500 group-hover:scale-110 shadow-lg`}>
                          {/* Icon gradient overlay */}
                          <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradientIcon} opacity-10 rounded-2xl group-hover:rounded-3xl group-hover:opacity-20 transition-all duration-500`}></div>
                          <Icon className={`w-10 h-10 lg:w-12 lg:h-12 text-[#0C1D40]`} />
                        </div>
                        {/* Pulse effect on hover */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradientIcon} rounded-2xl opacity-0 group-hover:opacity-20 group-hover:animate-pulse`}></div>
                      </div>
                      
                      {/* Enhanced number display */}
                      <div className="space-y-1">
                        <div className="text-4xl lg:text-5xl font-bold text-[#0C1D40] group-hover:scale-110 transition-transform duration-500">
                          <span className="group-hover:bg-gradient-to-r group-hover:from-pink-500 group-hover:to-purple-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-500">
                            {stat.number}
                          </span>
                        </div>
                        
                        {/* Label with better typography */}
                        <div className="text-base lg:text-lg font-semibold text-gray-800 tracking-wide">
                          {stat.label}
                        </div>
                        
                        {/* Description with subtle animation */}
                        <div className="text-xs lg:text-sm text-gray-600 font-medium leading-relaxed pt-2 opacity-80 group-hover:opacity-100 transition-opacity duration-500">
                          {stat.description}
                        </div>
                      </div>
                      
                      {/* Animated decorative dots */}
                      <div className="absolute top-4 right-4 flex space-x-1">
                        <div className={`w-1.5 h-1.5 bg-gradient-to-r ${stat.gradientIcon} rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300`}></div>
                        <div className={`w-1.5 h-1.5 bg-gradient-to-r ${stat.gradientIcon} rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500`}></div>
                        <div className={`w-1.5 h-1.5 bg-gradient-to-r ${stat.gradientIcon} rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700`}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Enhanced trust indicators section */}
        <div className="relative">
          <div className="bg-gradient-to-r from-white/80 via-pink-50/50 to-white/80 backdrop-blur-sm rounded-3xl p-8 border border-white/50 shadow-sm">
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-12">
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span className="text-sm font-semibold text-gray-700">Verified by pediatricians</span>
              </div>
              
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span className="text-sm font-semibold text-gray-700">HIPAA compliant</span>
              </div>
              
              <div className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-50 to-orange-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                </div>
                <span className="text-sm font-semibold text-gray-700">4.9/5 average rating</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
